import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { DiscountGroup } from '../../../../generated/generated-types';
import { DiscountGroupDocumentLineItemRelationWidgetComponent } from '../discount-group-document-line-item-relation-widget/discount-group-document-line-item-relation-widget.component';
import { DiscountGroupProductGroupRelationWidgetComponent } from '../discount-group-product-group-relation-widget/discount-group-product-group-relation-widget.component';
import { DiscountGroupProductRelationWidgetComponent } from '../discount-group-product-relation-widget/discount-group-product-relation-widget.component';

@Component({
  selector: 'app-formly-discount-group-relations-product-based-widget',
  templateUrl: './formly-discount-group-relations-product-based-widget.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TabsModule,
    DiscountGroupProductRelationWidgetComponent,
    DiscountGroupProductGroupRelationWidgetComponent,
    DiscountGroupDocumentLineItemRelationWidgetComponent,
    TranslatePipe,
    CommonModule,
    BlockableDiv,
    ButtonModule,
    TooltipModule,
  ]
})
export class FormlyDiscountGroupRelationsProductBasedWidgetComponent extends FieldType<FieldTypeConfig> {

  private readonly cdRef = inject(ChangeDetectorRef);

  isSaving = signal(false);

  /** Survives brief model/form clears after save (stream reload / route id sync). */
  private readonly stickyDiscountGroupId = signal<string | null>(null);

  get discountGroupId(): string | null {
    return this.model?.id ?? this.formControl?.value ?? this.stickyDiscountGroupId();
  }

  get isBlocked(): boolean {
    // Stay blocked for the whole save — avoids widgets flashing before save finishes.
    return this.isSaving() || !this.discountGroupId;
  }

  checkIfCanSave(): boolean {
    const component = this.options.formState.detailComponent as BaseDetailComponent<DiscountGroup>;
    return component.detailForm.invalid;
  }

  async saveToUnlockEditMode(): Promise<void> {
    const component = this.options.formState.detailComponent as BaseDetailComponent<DiscountGroup>;
    if (!component) {
      console.error('No detail component found to save DiscountGroup');
      return;
    }

    this.isSaving.set(true);
    this.cdRef.detectChanges();

    try {
      const saved = await component.save();
      const savedId = saved?.id ?? this.model?.id ?? null;
      if (savedId) {
        this.stickyDiscountGroupId.set(savedId);
      }
    } finally {
      this.isSaving.set(false);
      this.cdRef.detectChanges();
    }
  }

}
